/** Pure lifetime-counter definition, arithmetic and result validation; no answer or presentation dependencies. */
import * as z from 'zod';
export const repeatBuyingMetricDefinition = {
    id: 'repeat-buying',
    owner: 'shopify',
    reviewReference: 'docs/plans/shopify-insights-one-page.md',
    question: 'Among customers with a known lifetime order count of at least one, what share have ordered at least twice?',
    population: 'Latest customer profiles in the selected shop and connection; guest orders without a customer profile are excluded.',
    timeBasis: 'Lifetime order count recorded on each customer profile at the source sync time; no order-export observation window.',
    missingness: 'Null lifetime counters are unknown, excluded from the buyer denominator, and disclosed in coverage; invalid counters fail.',
    counterDomain: 'Nonnegative integer or null; negative and fractional values are invalid.',
    acceptedInputs: [],
    acceptedBreakdowns: [],
    unsupportedClaims: [
        'Same product among lifetime repeat buyers',
        'Repeat purchase within an observed time window',
        'Win-back eligibility or recommendation',
    ],
    basis: 'lifetime_profile_order_counter',
    metrics: {
        totalProfiles: {
            id: 'customer-profiles',
            unit: 'count',
            population: 'All current customer profiles in the selected connection.',
        },
        zeroOrderProfiles: {
            id: 'customer-zero-order-profiles',
            unit: 'count',
            population: 'Profiles with a known lifetime order count equal to zero.',
        },
        oneTimeBuyers: {
            id: 'customer-one-time-buyers',
            unit: 'count',
            population: 'Profiles with a known lifetime order count equal to one.',
        },
        repeatBuyers: {
            id: 'customer-repeat-buyers',
            unit: 'count',
            population: 'Profiles with a known lifetime order count at least two.',
        },
        unknownOrderProfiles: {
            id: 'customer-unknown-order-profiles',
            unit: 'count',
            population: 'Profiles with a null lifetime order count.',
        },
        knownBuyers: {
            id: 'customer-known-buyers',
            unit: 'count',
            population: 'Profiles with a known lifetime order count at least one.',
            formula: { addends: ['oneTimeBuyers', 'repeatBuyers'] },
        },
        repeatShare: {
            id: 'customer-repeat-share',
            unit: 'proportion',
            population: 'Repeat buyers among known lifetime buyers.',
            formula: { numerator: 'repeatBuyers', denominator: 'knownBuyers' },
            exclusions: ['zeroOrderProfiles', 'unknownOrderProfiles'],
            zeroBase: 'unavailable',
            zeroNumeratorWithPositiveBase: 0,
        },
    },
};
const metrics = repeatBuyingMetricDefinition.metrics;
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const measurement = (metric) => z.strictObject({
    definition: z.strictObject({ id: z.literal(metric.id) }),
    unit: z.literal(metric.unit),
    value: count,
});
const share = z.discriminatedUnion('state', [
    z.strictObject({ state: z.literal('available'), value: z.number().min(0).max(1) }),
    z.strictObject({ state: z.literal('unavailable'), reason: z.literal('zero_base') }),
]);
export const repeatBuyingMetricsSchema = z
    .strictObject({
    counts: z.strictObject({
        totalProfiles: count,
        zeroOrderProfiles: count,
        oneTimeBuyers: count,
        repeatBuyers: count,
        unknownOrderProfiles: count,
        knownBuyers: count,
    }),
    repeatShare: share,
    coverage: z.strictObject({ knownOrderProfiles: count, unknownOrderProfiles: count }),
    measurements: z.strictObject({
        totalProfiles: measurement(metrics.totalProfiles),
        knownBuyers: measurement(metrics.knownBuyers),
        repeatBuyers: measurement(metrics.repeatBuyers),
        repeatShare: z.strictObject({
            definition: z.strictObject({ id: z.literal(metrics.repeatShare.id) }),
            unit: z.literal(metrics.repeatShare.unit),
            result: share,
        }),
    }),
})
    .superRefine((r, ctx) => {
    const c = r.counts;
    const valid = c.totalProfiles === c.zeroOrderProfiles + c.oneTimeBuyers + c.repeatBuyers + c.unknownOrderProfiles &&
        c.knownBuyers === c[metrics.knownBuyers.formula.addends[0]] + c[metrics.knownBuyers.formula.addends[1]] &&
        r.coverage.knownOrderProfiles === c.totalProfiles - c.unknownOrderProfiles &&
        r.coverage.unknownOrderProfiles === c.unknownOrderProfiles &&
        r.measurements.totalProfiles.value === c.totalProfiles &&
        r.measurements.knownBuyers.value === c.knownBuyers &&
        r.measurements.repeatBuyers.value === c.repeatBuyers &&
        (c.knownBuyers === 0
            ? r.repeatShare.state === 'unavailable'
            : r.repeatShare.state === 'available' &&
                r.repeatShare.value ===
                    c[metrics.repeatShare.formula.numerator] / c[metrics.repeatShare.formula.denominator]) &&
        JSON.stringify(r.measurements.repeatShare.result) === JSON.stringify(r.repeatShare);
    if (!valid)
        ctx.addIssue({
            code: 'custom',
            message: 'INSIGHT_PRESENTATION_INVALID: populations, coverage or share do not reconcile',
        });
});
/** Preserve each metric's literal ID instead of widening it to every count metric. */
function countMeasurement(metric, value) {
    return { definition: { id: metric.id }, unit: metric.unit, value };
}
export function calculateRepeatBuyingMetrics(input) {
    const counts = {
        totalProfiles: input.totalProfiles,
        zeroOrderProfiles: input.zeroOrderProfiles,
        oneTimeBuyers: input.oneTimeBuyers,
        repeatBuyers: input.repeatBuyers,
        unknownOrderProfiles: input.unknownOrderProfiles,
        knownBuyers: input[metrics.knownBuyers.formula.addends[0]] + input[metrics.knownBuyers.formula.addends[1]],
    };
    const repeatShare = counts.knownBuyers
        ? {
            state: 'available',
            value: counts[metrics.repeatShare.formula.numerator] / counts[metrics.repeatShare.formula.denominator],
        }
        : { state: 'unavailable', reason: 'zero_base' };
    return repeatBuyingMetricsSchema.parse({
        counts,
        repeatShare,
        coverage: {
            knownOrderProfiles: counts.totalProfiles - counts.unknownOrderProfiles,
            unknownOrderProfiles: counts.unknownOrderProfiles,
        },
        measurements: {
            totalProfiles: countMeasurement(metrics.totalProfiles, counts.totalProfiles),
            knownBuyers: countMeasurement(metrics.knownBuyers, counts.knownBuyers),
            repeatBuyers: countMeasurement(metrics.repeatBuyers, counts.repeatBuyers),
            repeatShare: {
                definition: { id: metrics.repeatShare.id },
                unit: metrics.repeatShare.unit,
                result: repeatShare,
            },
        },
    });
}
//# sourceMappingURL=repeat-buying-metrics.js.map